import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountingService } from '../../../services/accounting.service';
import { CardModule } from 'primeng/card';
import { ChartModule } from 'primeng/chart';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';

@Component({
  selector: 'app-accounting-dashboard',
  standalone: true,
  imports: [CommonModule, CardModule, ChartModule, TableModule, ButtonModule],
  template: `
    <div class="p-4">
      <div class="flex justify-content-between align-items-center mb-4">
        <h1 class="text-3xl font-bold m-0">CashZap Dashboard</h1>
        <p-button label="New Transaction" icon="pi pi-plus" (onClick)="router.navigate(['/transactions'])"></p-button>
      </div>

      <div class="grid mb-4">
        <div class="col-12 md:col-4" *ngFor="let acc of accounts">
          <p-card [header]="acc.name" [subheader]="acc.type | uppercase">
            <h2 [class.text-red-500]="acc.current_balance < 0" class="text-2xl font-bold">
              {{ acc.current_balance | currency:acc.currency }}
            </h2>
          </p-card>
        </div>
      </div>

      <div class="grid mb-4">
        <div class="col-12 lg:col-8">
          <p-card header="Cash Flow (Last 6 Months)">
            <p-chart type="bar" [data]="chartData" [options]="chartOptions" (onDataSelect)="onChartClick($event)"></p-chart>
          </p-card>
        </div>
        <div class="col-12 lg:col-4">
          <p-card header="Top Expenses">
             <p-chart type="doughnut" [data]="pieData" [options]="pieOptions" (onDataSelect)="onPieClick($event)"></p-chart>
          </p-card>
        </div>
      </div>

      <p-card header="Recent Transactions">
        <p-table [value]="recentTransactions" [rows]="5" styleClass="p-datatable-sm">
          <ng-template pTemplate="header">
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Account</th>
              <th class="text-right">Amount</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-tx>
            <tr>
              <td>{{ tx.date | date }}</td>
              <td>{{ tx.description }}</td>
              <td>{{ getAccountName(tx.from_account_id) }}</td>
              <td class="text-right font-bold" [class.text-red-500]="tx.amount < 0">
                {{ tx.amount | currency }}
              </td>
            </tr>
          </ng-template>
        </p-table>
        <div class="flex justify-content-end mt-3">
          <p-button label="View All Transactions" [text]="true" (onClick)="router.navigate(['/transactions'])"></p-button>
        </div>
      </p-card>
    </div>
  `
})
export class DashboardComponent implements OnInit {
  accounts: any[] = [];
  allTransactions: any[] = [];
  recentTransactions: any[] = [];
  chartData: any;
  chartOptions: any;
  pieData: any;
  pieOptions: any;

  monthIndices: number[] = [];
  years: number[] = [];
  pieAccountIds: string[] = [];

  constructor(private accountingService: AccountingService, public router: Router) { }

  ngOnInit() {
    this.accountingService.getAccounts().subscribe(data => this.accounts = data);
    this.accountingService.getTransactions().subscribe(data => {
      this.allTransactions = data;
      this.recentTransactions = data.slice(0, 5);
      this.initCharts();
    });
  }

  getAccountName(id: string): string {
    const acc = this.accounts.find(a => a.id === id);
    return acc ? acc.name : id;
  }

  initCharts() {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const labels = [];
    const incomeData = [];
    const expenseData = [];

    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const mIdx = d.getMonth();
      const year = d.getFullYear();

      this.monthIndices.push(mIdx);
      this.years.push(year);
      labels.push(monthNames[mIdx]);

      // Calculate income/expenses for this month
      const monthTxs = this.allTransactions.filter(tx => {
        const txD = new Date(tx.date);
        return txD.getMonth() === mIdx && txD.getFullYear() === year;
      });

      const income = monthTxs.filter(tx => {
        const fromAcc = this.accounts.find(a => a.id === tx.from_account_id);
        return fromAcc?.type === 'revenue';
      }).reduce((sum, tx) => sum + tx.amount, 0);

      const expense = monthTxs.filter(tx => {
        const toAcc = this.accounts.find(a => a.id === tx.to_account_id);
        return toAcc?.type === 'expense';
      }).reduce((sum, tx) => sum + tx.amount, 0);

      incomeData.push(income);
      expenseData.push(expense);
    }

    this.chartData = {
      labels: labels,
      datasets: [
        {
          label: 'Income',
          backgroundColor: '#22c55e',
          data: incomeData
        },
        {
          label: 'Expenses',
          backgroundColor: '#ef4444',
          data: expenseData
        }
      ]
    };

    this.chartOptions = {
      plugins: {
        legend: { labels: { color: '#ebedef' } }
      },
      scales: {
        x: { ticks: { color: '#ebedef' } },
        y: { ticks: { color: '#ebedef' } }
      }
    };

    // Top Expenses Pie
    const expenseMap: Record<string, { id: string, amount: number }> = {};
    this.allTransactions.forEach(tx => {
      const toAcc = this.accounts.find(a => a.id === tx.to_account_id);
      if (toAcc?.type === 'expense') {
        if (!expenseMap[toAcc.name]) {
          expenseMap[toAcc.name] = { id: toAcc.id, amount: 0 };
        }
        expenseMap[toAcc.name].amount += tx.amount;
      }
    });

    const sortedExpenses = Object.entries(expenseMap)
      .sort((a, b) => b[1].amount - a[1].amount)
      .slice(0, 5);

    this.pieAccountIds = sortedExpenses.map(e => e[1].id);
    this.pieData = {
      labels: sortedExpenses.map(e => e[0]),
      datasets: [
        {
          data: sortedExpenses.map(e => e[1].amount),
          backgroundColor: ['#3b82f6', '#f59e0b', '#ef4444', '#10b981', '#a855f7']
        }
      ]
    };

    this.pieOptions = {
      plugins: {
        legend: { position: 'bottom', labels: { color: '#ebedef' } }
      }
    };
  }

  onChartClick(event: any) {
    const datasetIndex = event.element.datasetIndex; // 0 for Income, 1 for Expenses
    const index = event.element.index; // month index in our local array

    const type = datasetIndex === 0 ? 'income' : 'expense';
    const month = this.monthIndices[index];
    const year = this.years[index];

    this.router.navigate(['/transactions/detail'], {
      queryParams: { month, year, type }
    });
  }

  onPieClick(event: any) {
    const index = event.element.index;
    const accountId = this.pieAccountIds[index];

    this.router.navigate(['/transactions/detail'], {
      queryParams: { accountId, type: 'expense' }
    });
  }
}
